import React, { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { FaEye, FaEyeSlash, FaKey, FaLock } from "react-icons/fa";
import { supabase } from "../supabaseClient";
import nhsLogo from "../assets/nhs-logo.png";

export default function ChangeTemporaryPassword({ session, profile }) {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  if (!session) return <Navigate to="/login" replace />;
  if (profile && profile.must_change_password !== true) {
    return <Navigate to="/intranet/portal" replace />;
  }

  const submit = async (event) => {
    event.preventDefault();
    setError("");

    if (password.length < 8) {
      setError("Your new password must contain at least 8 characters.");
      return;
    }
    if (password !== confirmPassword) {
      setError("The passwords do not match.");
      return;
    }

    setSaving(true);
    try {
      const { error: passwordError } = await supabase.auth.updateUser({ password });
      if (passwordError) throw passwordError;

      const { error: profileError } = await supabase.rpc(
        "complete_first_login_password_change",
      );
      if (profileError) throw profileError;

      // A refreshed login ensures every tab receives a token minted after the
      // temporary-password state was cleared.
      await supabase.auth.refreshSession();
      navigate("/intranet/portal", { replace: true });
      window.location.reload();
    } catch (err) {
      setError(err?.message || "Your password could not be changed.");
      setSaving(false);
    }
  };

  return (
    <div className="pguh-auth-page">
      <header className="pguh-public-header">
        <div className="nhsuk-width-container pguh-public-header__inner">
          <img src={nhsLogo} alt="NHS" />
          <div>
            <strong>Royal University Hospitals Trust</strong>
            <span>PGUH Services</span>
          </div>
        </div>
      </header>

      <main id="main-content" tabIndex="-1" className="pguh-auth-main">
        <div className="nhsuk-width-container pguh-auth-grid">
          <section className="pguh-auth-intro">
            <span className="pguh-eyebrow">First sign in</span>
            <h1 className="nhsuk-heading-xl">Create your permanent password</h1>
            <p className="nhsuk-body-l">
              Management created this account with a temporary password. You must
              replace it before accessing staff services.
            </p>
            <div className="nhsuk-inset-text pguh-security-note">
              <FaLock aria-hidden="true" />
              <p>
                Your temporary password is not stored in the PGUH profile database.
                Choose a password you do not use for another service.
              </p>
            </div>
          </section>

          <section className="pguh-auth-card">
            <div className="pguh-first-password-icon"><FaKey aria-hidden="true" /></div>
            <h2 className="nhsuk-heading-l">Change temporary password</h2>
            <p className="nhsuk-body-s">
              Signed in as <strong>{session.user.email}</strong>
            </p>

            {error && <div className="nhsuk-error-summary" role="alert"><p>{error}</p></div>}

            <form onSubmit={submit}>
              <div className="nhsuk-form-group">
                <label className="nhsuk-label nhsuk-label--s" htmlFor="new-password">New password</label>
                <div className="pguh-password-field">
                  <input
                    id="new-password"
                    className="nhsuk-input"
                    type={showPassword ? "text" : "password"}
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  <button type="button" onClick={() => setShowPassword((value) => !value)} aria-label={showPassword ? "Hide password" : "Show password"}>
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
                <div className="nhsuk-hint">Use at least 8 characters.</div>
              </div>

              <div className="nhsuk-form-group">
                <label className="nhsuk-label nhsuk-label--s" htmlFor="confirm-password">Confirm new password</label>
                <input
                  id="confirm-password"
                  className="nhsuk-input"
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </div>

              <button className="nhsuk-button" type="submit" disabled={saving}>
                {saving ? "Changing password..." : "Set permanent password"}
              </button>
            </form>
          </section>
        </div>
      </main>
    </div>
  );
}
